/**
 * Include icons from icon packs in the export
 *
 * Icons are stored in external packs, not in the model itself.
 * Solution: Gather all referenced icons from packs and include them in export
 */

import { Model } from '../../../types';
import { iconPackManager } from '@features/components/iconPacks';
import {
    ExportHandler,
    ExportResult,
    ExportConfig,
    ExportProgressCallback,
} from '../exportTypes';
import {
    generateFilename,
    createExportMetadata,
} from '../exportUtils';

export interface JSONExportData {
    version: string;
    metadata: {
        exportDate: string;
        appVersion: string;
        diagramName?: string;
    };
    model: Model;
}

export class JSONExportHandler implements ExportHandler {
    public readonly format = 'json' as const;
    private readonly APP_VERSION = '1.0.0';

    async export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            onProgress?.(0, 'Preparing JSON export...');
            onProgress?.(30, 'Serializing diagram data...');

            const exportData: JSONExportData = {
                version: this.APP_VERSION,
                metadata: {
                    exportDate: new Date().toISOString(),
                    appVersion: this.APP_VERSION,
                    diagramName: model.title,
                },
                model: this.sanitizeAndEnrichModel(model),
            };

            onProgress?.(70, 'Converting to JSON...');

            const jsonString = JSON.stringify(exportData, null, 2);
            const filename = config.filename || generateFilename(model.title || 'flowi-diagram', 'json');
            const rect = element.getBoundingClientRect();
            const metadata = createExportMetadata(
                config,
                rect.width,
                rect.height,
                model.title
            );

            onProgress?.(100, 'Export complete!');

            return {
                success: true,
                data: jsonString,
                filename,
                format: 'json',
                metadata,
            };
        } catch (error) {
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * ✅ PROPER FIX: Sanitize AND enrich model with icons from packs
     */
    private sanitizeAndEnrichModel(model: Model): Model {
        const sanitized = JSON.parse(JSON.stringify(model));

        // Ensure required arrays exist
        sanitized.icons = sanitized.icons || [];
        sanitized.colors = sanitized.colors || [];
        sanitized.items = sanitized.items || [];

        if (!sanitized.views || !Array.isArray(sanitized.views) || sanitized.views.length === 0) {
            sanitized.views = [{
                id: 'default-view',
                name: 'Main View',
                items: [],
                connectors: [],
                rectangles: [],
                textBoxes: []
            }];
        }

        if (!sanitized.title || sanitized.title.trim() === '') {
            sanitized.title = 'Untitled';
        }

        if (sanitized.title.length > 30) {
            sanitized.title = sanitized.title.substring(0, 30);
        }

        if (sanitized.version === undefined) {
            sanitized.version = '';
        }

        // ✅ CRITICAL: Gather all referenced icon IDs
        const referencedIconIds = new Set<string>();

        if (Array.isArray(sanitized.items)) {
            sanitized.items.forEach((item: any) => {
                // Clean up runtime properties
                delete item._selected;
                delete item._hovered;
                delete item._temp;

                // Collect icon references
                if (item.icon) {
                    referencedIconIds.add(item.icon);
                }
            });
        }

        // ✅ Fetch icons from icon packs and add to model
        console.log('📦 Gathering icons from packs...');
        referencedIconIds.forEach(iconId => {
            try {
                const iconDef = iconPackManager.getIconById(iconId);
                if (iconDef) {
                    // Convert IconDefinition to model Icon format
                    const modelIcon = {
                        id: iconDef.id,
                        name: iconDef.name,
                        url: iconDef.svgPath || '', // SVG path for local icons
                        isIsometric: iconDef.isIsometric,
                        collection: iconDef.pack // Keep pack info
                    };

                    // Only add if not already in icons array
                    if (!sanitized.icons.find((icon: any) => icon.id === iconDef.id)) {
                        sanitized.icons.push(modelIcon);
                        console.log(`  ✓ Added icon: ${iconDef.name} (${iconDef.id})`);
                    }
                } else {
                    console.warn(`  ⚠️  Icon not found in packs: ${iconId}`);
                    // Icon doesn't exist - validation will fail
                    // But we'll let validation handle it
                }
            } catch (error) {
                console.error(`  ❌ Error fetching icon ${iconId}:`, error);
            }
        });

        console.log(`📊 Included ${sanitized.icons.length} icons in export`);

        // Clean up views
        if (Array.isArray(sanitized.views)) {
            sanitized.views.forEach((view: any) => {
                if (!view.id) view.id = `view-${Date.now()}`;
                if (!view.name) view.name = 'Untitled View';

                view.items = Array.isArray(view.items) ? view.items : [];
                view.connectors = Array.isArray(view.connectors) ? view.connectors : [];
                view.rectangles = Array.isArray(view.rectangles) ? view.rectangles : [];
                view.textBoxes = Array.isArray(view.textBoxes) ? view.textBoxes : [];

                view.items.forEach((viewItem: any) => {
                    delete viewItem._selected;
                    delete viewItem._hovered;
                    delete viewItem._temp;
                });

                view.connectors.forEach((connector: any) => {
                    delete connector._selected;
                    delete connector._hovered;
                    delete connector._temp;
                    connector.anchors = Array.isArray(connector.anchors) ? connector.anchors : [];
                    connector.anchors.forEach((anchor: any) => {
                        delete anchor._selected;
                        delete anchor._hovered;
                        delete anchor._temp;
                    });
                });

                view.rectangles.forEach((rectangle: any) => {
                    delete rectangle._selected;
                    delete rectangle._hovered;
                    delete rectangle._temp;
                });

                view.textBoxes.forEach((textBox: any) => {
                    delete textBox._selected;
                    delete textBox._hovered;
                    delete textBox._temp;
                });
            });
        }

        return sanitized;
    }

    static async importJSON(jsonString: string): Promise<Model> {
        try {
            const data: JSONExportData = JSON.parse(jsonString);

            if (!data.model) {
                throw new Error('Invalid JSON export format: missing model data');
            }

            if (data.version && !this.isCompatibleVersion(data.version)) {
                console.warn(`JSON export version ${data.version} may not be fully compatible`);
            }

            return data.model;
        } catch (error) {
            throw new Error(`Failed to import JSON: ${(error as Error).message}`);
        }
    }

    private static isCompatibleVersion(version: string): boolean {
        const [major] = version.split('.').map(Number);
        const [currentMajor] = '1.0.0'.split('.').map(Number);
        return major === currentMajor;
    }

    validateConfig(config: ExportConfig): boolean {
        return config.format === 'json';
    }
}

export const createJSONExportHandler = (): JSONExportHandler => {
    return new JSONExportHandler();
};

export const importFromJSON = async (file: File): Promise<Model> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const content = e.target?.result as string;
                const model = await JSONExportHandler.importJSON(content);
                resolve(model);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsText(file);
    });
};